import { createContext, useContext, type ReactNode } from 'react';
import {
  getContentScannerById,
  getContentScanners,
  type ContentScannerDescriptor,
} from '../../../client/content-scanners';

/**
 * When a `text` scanner takes over an `inlineCode` node, the `<code>` wrapper
 * the surface would normally render is replaced by the scanner's component. If
 * the scanner later "demotes" (e.g. the file path doesn't exist on the devbox),
 * it renders plain text — but the `<code>` wrapper is already gone, stripping
 * the code formatting the author wrote. This context carries the surface's
 * inline-code wrapper so a demoting scanner can restore it.
 */
const InlineCodeFallbackContext = createContext<((content: ReactNode) => ReactNode) | null>(null);

const InlineCodeFallbackProvider = InlineCodeFallbackContext.Provider;

export function useInlineCodeFallback(): ((content: ReactNode) => ReactNode) | null {
  return useContext(InlineCodeFallbackContext);
}

/**
 * Custom URL scheme the remark plugin writes onto `link` nodes for `text`
 * scanner matches. The `a` slot detects it and swaps in the scanner's
 * component instead of navigating. Shape: `reflex-scan:<id>:<encoded match>`.
 *
 * Piggybacking on react-markdown's existing `a` slot (instead of a custom
 * mdast node) means no extra component-mapping is needed — the same trick the
 * old `reflex-file:` scheme used, now generalized.
 */
export const REFLEX_SCAN_SCHEME = 'reflex-scan:';

/** Build a `reflex-scan:` URL for a text-scanner match. */
export function encodeScanUrl(id: string, match: string): string {
  return `${REFLEX_SCAN_SCHEME}${id}:${encodeURIComponent(match)}`;
}

/** Parse a `reflex-scan:` URL back into its scanner id and matched text. */
export function parseScanUrl(href: string): { id: string; match: string } | null {
  if (!href.startsWith(REFLEX_SCAN_SCHEME)) return null;
  const rest = href.slice(REFLEX_SCAN_SCHEME.length);
  // The match is percent-encoded (never a raw ':'), so the LAST ':' is the
  // id/match separator — which lets scanner ids use ':' for namespacing
  // (e.g. 'sandbox-filesystem:absolute-path').
  const sep = rest.lastIndexOf(':');
  if (sep <= 0) return null;
  const id = rest.slice(0, sep);
  try {
    return { id, match: decodeURIComponent(rest.slice(sep + 1)) };
  } catch {
    return null;
  }
}

/**
 * Capture groups for `value` under `regex`, stateless regardless of the
 * regex's `g`/`y` flags. Returns group 1 at index 0; missing groups are `''`.
 */
export function matchGroups(regex: RegExp, value: string): string[] {
  const clone = new RegExp(regex.source, regex.flags.replace(/[gy]/g, ''));
  const m = clone.exec(value);
  return m ? m.slice(1).map((g) => g ?? '') : [];
}

/**
 * First registered *inline* `url` scanner whose regex matches `href`, if any.
 * `trailing` scanners deliberately do not claim the anchor — their whole point
 * is that the link keeps the surface's own styling (see
 * {@link renderTrailingContentScanners}).
 */
export function findUrlScanner(href: string): ContentScannerDescriptor | undefined {
  return getContentScanners('url').find(
    (s) => (s.placement ?? 'inline') === 'inline' && s.regex.test(href),
  );
}

/**
 * Render the `trailing` `url` scanners for `hrefs` — the unique http(s) link
 * targets of one markdown block, in source order (see
 * `remark-trailing-links.ts`). `MarkdownContent` drops the result in after the
 * prose, so a link preview stacks under the message instead of interrupting
 * the sentence that links it.
 *
 * Order and dedupe come from the source order of the links themselves, so
 * nothing here depends on when a card's data resolves. A scanner that renders
 * nothing (no preview for this URL) contributes no DOM at all — markdown
 * blocks style their last child with `last:mb-0`, so an empty placeholder
 * after the prose would silently restore that margin on every message.
 *
 * `inView` says whether the block is on screen; it rides through to each
 * component so a card can defer its fetch.
 */
export function renderTrailingContentScanners(
  hrefs: readonly string[],
  inView: boolean,
): ReactNode[] {
  const scanners = getContentScanners('url').filter((s) => s.placement === 'trailing');
  if (scanners.length === 0) return [];
  const nodes: ReactNode[] = [];
  for (const href of hrefs) {
    const scanner = scanners.find((s) => s.regex.test(href));
    if (!scanner) continue;
    const Component = scanner.Component;
    nodes.push(
      <Component
        key={`${scanner.id}:${href}`}
        match={href}
        groups={matchGroups(scanner.regex, href)}
        inView={inView}
      />,
    );
  }
  return nodes;
}

/**
 * First registered `text` scanner that claims the ENTIRE trimmed `value`.
 * Used to treat a value we already have in hand (a known file path, an inline
 * code span whose whole content is a path) as a scanner match, rather than
 * discovering matches inside free text. Whole-string only, so `run /a/b.sh`
 * (a command that merely contains a path) is left alone.
 */
export function matchWholeTextScanner(
  value: string,
): { scanner: ContentScannerDescriptor; match: string } | null {
  const trimmed = value.trim();
  if (!trimmed) return null;
  for (const scanner of getContentScanners('text')) {
    // Stateless clone so a shared `g`/`y` regex's `lastIndex` can't leak.
    const clone = new RegExp(scanner.regex.source, scanner.regex.flags.replace(/[gy]/g, ''));
    const m = clone.exec(trimmed);
    if (m && m[0] === trimmed) return { scanner, match: trimmed };
  }
  return null;
}

/**
 * Render an inline-code span through a `text` scanner when its whole content
 * is a scanner match (e.g. an absolute file path in backticks becomes the
 * filesystem preview badge); otherwise render `fallback()` (the plain
 * `<code>` each surface owns). The remark plugin can't reach `inlineCode`
 * nodes — they carry a raw `value`, not child text nodes — so this render-time
 * hook is where backticked paths get linkified.
 */
export function renderContentScannerInlineCode(
  text: string,
  wrapInCode: (content: ReactNode) => ReactNode,
): ReactNode {
  const hit = matchWholeTextScanner(text);
  if (!hit) return wrapInCode(text);
  const Component = hit.scanner.Component;
  return (
    <InlineCodeFallbackProvider value={wrapInCode}>
      <Component match={hit.match} groups={matchGroups(hit.scanner.regex, hit.match)} />
    </InlineCodeFallbackProvider>
  );
}

/**
 * Shared `a`-slot dispatch used by both the chat and prose markdown
 * component sets. Resolution order:
 *  1. `reflex-scan:` href → render the matched `text` scanner's component.
 *  2. `http(s)` href matching a registered `url` scanner → render it (with the
 *     link label as children).
 *  3. A non-`http(s)` href that is itself a whole `text`-scanner match (e.g.
 *     an agent writing `[name](/home/user/screenshots/x.png)`) → render the
 *     scanner's component, so an explicit link to a path renders the same
 *     inline preview/chip a bare path in prose would, instead of a dead
 *     same-origin `<a>` that 404s against the app origin.
 *  4. Otherwise → `fallback()` (the plain styled `<a>` each surface owns).
 *
 * `remarkContentScanners` skips text inside `link` nodes (no double-linkify),
 * so case 3 is the only place an author-written `[label](/abs/path)` link can
 * reach a scanner. Whole-string only, so a command-shaped href like
 * `run /a/b.sh` is left alone.
 *
 * Reads the registry at render time, so a scanner that disappears between
 * parse and render degrades cleanly to the matched text / plain link.
 */
export function renderContentScannerAnchor(
  href: string | undefined,
  children: ReactNode,
  fallback: () => ReactNode,
): ReactNode {
  if (href && href.startsWith(REFLEX_SCAN_SCHEME)) {
    const parsed = parseScanUrl(href);
    if (!parsed) return fallback();
    const scanner = getContentScannerById(parsed.id);
    if (scanner && scanner.target === 'text') {
      const Component = scanner.Component;
      return <Component match={parsed.match} groups={matchGroups(scanner.regex, parsed.match)} />;
    }
    // Scanner unregistered since parse — render the matched text verbatim.
    return <>{parsed.match}</>;
  }
  if (href && /^https?:/i.test(href)) {
    const scanner = findUrlScanner(href);
    if (scanner) {
      const Component = scanner.Component;
      return (
        <Component match={href} groups={matchGroups(scanner.regex, href)}>
          {children}
        </Component>
      );
    }
  }
  // An explicit markdown link whose target is itself a whole text-scanner
  // match (typically an absolute file path the agent linked by name). Route
  // it to the scanner so it renders the inline preview/chip a bare path
  // would, instead of a plain same-origin anchor that 404s.
  const wholeText = href ? matchWholeTextScanner(href) : null;
  if (wholeText) {
    const Component = wholeText.scanner.Component;
    return (
      <Component
        match={wholeText.match}
        groups={matchGroups(wholeText.scanner.regex, wholeText.match)}
      >
        {children}
      </Component>
    );
  }
  return fallback();
}
